import type { Platform, SubmissionStatus, VerificationCheckType } from './enums.js';

/**
 * The submission rules both sides compute the same way, and the constants the
 * Submit screen needs at runtime.
 *
 * NO ZOD HERE, ON PURPOSE — same reason as `campaigns.ts` and `connections.ts`.
 * The request and response schemas live in `./contracts.ts`; eagerly built zod
 * objects do not tree-shake, so a barrel that re-exported them would ship every
 * server-side rule to the browser. This file is plain types, plain constants
 * and pure functions, all of which the Submit screen genuinely uses.
 */

/* ------------------------------------------------------------------ */
/* The checks, in the order they run                                   */
/* ------------------------------------------------------------------ */

/**
 * The automatic checks, in the order phase 3 runs them, stopping at the first
 * failure.
 *
 * THE DATABASE'S NAMES AND THE PLAN'S NAMES ARE NOT THE SAME STRINGS, and this
 * is the map between them. `VerificationCheckType` was ported verbatim from the
 * Postgres scaffold in phase 0 and is pinned by a CHECK constraint on the
 * column, so adding `POST_EXISTS_PUBLIC` as a second spelling of
 * `PUBLIC_VISIBILITY` would mean a table rebuild to give one concept two names.
 * The stored value is the scaffold's; `planName` is the name the build plan and
 * this codebase's prose use, and it is written into every check's evidence so a
 * row can be read back without this table in front of you.
 */
export interface SubmissionCheckMeta {
  /** The value stored in `VerificationCheck.checkType`. */
  checkType: VerificationCheckType;
  /** The name the plan gives this check, carried in the evidence JSON. */
  planName: string;
  /** What the creator watching the checks run is shown. */
  label: string;
  /**
   * A failure here is a HOLD for the operator rather than a rejection. Captions
   * can be edited after posting, so "the hashtag is missing" is a question for
   * a person, not a verdict that costs a creator the clip they already cut.
   */
  holdsRatherThanRejects?: boolean;
}

export const SUBMISSION_CHECKS: readonly SubmissionCheckMeta[] = Object.freeze([
  {
    checkType: 'PLATFORM_MATCH',
    planName: 'PLATFORM_ALLOWED',
    label: 'The platform is one this campaign accepts',
  },
  {
    checkType: 'PUBLIC_VISIBILITY',
    planName: 'POST_EXISTS_PUBLIC',
    label: 'The post exists and is public',
  },
  {
    checkType: 'ACCOUNT_MATCH',
    planName: 'ACCOUNT_OWNERSHIP',
    label: 'The post is on your connected account',
  },
  /**
   * THE FOLLOWER FLOOR, ABOUT THE ACCOUNT THE POST IS ON.
   *
   * `evaluateEligibility` lets a creator take a campaign when ANY of their
   * accounts clears the floor, which with five accounts per platform is not
   * the same question as "does the account this clip is on clear it". This is
   * that question, and it is a separate row with its own evidence because the
   * two can disagree: the card says "You qualify" and this one says which
   * account it was about.
   *
   * It shares `ACCOUNT_MATCH` with ownership because `VerificationCheck`'s
   * `checkType` carries a CHECK constraint written in migration 0000 and
   * widening it on D1 would mean rebuilding a table that owns
   * `PostSubmission` — the rebuild every migration since 0002 refuses. The
   * PLAN NAME is what tells the two rows apart, which is the same thing
   * phase 4 does with `ACCOUNT_AGE` and `CONCENTRATION` on
   * `NEW_ACCOUNT_CONCENTRATION`, and why the evidence has always carried
   * `check`.
   */
  {
    checkType: 'ACCOUNT_MATCH',
    planName: 'ACCOUNT_ELIGIBILITY',
    label: 'That account meets the campaign’s follower floor',
  },
  {
    checkType: 'PUBLICATION_WINDOW',
    planName: 'PUBLISHED_AFTER_START',
    label: 'It was posted after the campaign started',
  },
  {
    checkType: 'DUPLICATE_CHECK',
    planName: 'NOT_DUPLICATE',
    label: 'It has not been submitted before',
  },
  {
    checkType: 'CAPTION_REQUIREMENTS',
    planName: 'CAPTION_REQUIREMENTS',
    label: 'The caption carries what the campaign asks for',
    holdsRatherThanRejects: true,
  },
  {
    checkType: 'DISCLOSURE_REQUIREMENTS',
    planName: 'DISCLOSURE_REQUIREMENTS',
    label: 'The post is disclosed as paid',
    holdsRatherThanRejects: true,
  },
] as const satisfies readonly SubmissionCheckMeta[]);

/**
 * The order the rows are written in, for a screen that wants to lay them out.
 *
 * PLAN NAMES, not check types: two of these share `ACCOUNT_MATCH` (ownership
 * and the follower floor) and a list keyed on the column would collapse them
 * into one row that says the wrong thing about whichever of the two ran last.
 */
export const SUBMISSION_CHECK_ORDER: readonly string[] = Object.freeze(
  SUBMISSION_CHECKS.map((c) => c.planName),
);

/**
 * The meta for a `checkType`, which is the COARSE key.
 *
 * Where two rules share one check type this returns the first — ownership for
 * `ACCOUNT_MATCH` — so a caller that has only the column still gets a sensible
 * answer. A caller that has the row's evidence should use
 * `submissionCheckMetaByPlan` with its `check` field, which is exact.
 */
export function submissionCheckMeta(
  checkType: VerificationCheckType,
): SubmissionCheckMeta | undefined {
  return SUBMISSION_CHECKS.find((c) => c.checkType === checkType);
}

/** The meta for a plan name — the name the evidence JSON always carries. */
export function submissionCheckMetaByPlan(planName: string): SubmissionCheckMeta | undefined {
  return SUBMISSION_CHECKS.find((c) => c.planName === planName);
}

/** What a creator is told each status means. */
export const SUBMISSION_STATUS_LABELS: Readonly<Record<SubmissionStatus, string>> = Object.freeze({
  PENDING_CHECKS: 'Running the checks',
  IN_REVIEW: 'In content review',
  VERIFIED: 'Approved',
  REJECTED: 'Rejected',
  HELD: 'Held for review',
});

/* ------------------------------------------------------------------ */
/* The id the uniqueness index is keyed on                             */
/* ------------------------------------------------------------------ */

/**
 * The one spelling of a post id that `PostSubmission.normalizedPostId` may
 * hold, so that one post is one row.
 *
 * THE INDEX IS A STRING COMPARISON, which means any second spelling of the same
 * post is a second live claim and the "one post counts once" rule is gone.
 * `resolvePostUrl` already strips the query string, the host variant and the
 * handle in the path, and it deliberately preserves the case of an Instagram
 * shortcode and a YouTube id because THOSE ARE CASE-SENSITIVE: `oC8w-tosXOw`
 * and `oc8w-tosxow` are different videos, and lower-casing them here would
 * collide two real posts into one claim, which is the worse failure.
 *
 * TikTok is the one that needs normalising. Its `aweme_id` is a decimal number
 * and the resolver accepts `\d{5,30}`, so `/video/07687693747122933023` and
 * `/video/7687693747122933023` are the same video reached through two strings —
 * and TikTok's own endpoint answers for both, because it parses the id as an
 * integer. Leading zeros are therefore stripped: without this, pasting one
 * extra `0` mints a fresh claim on a post somebody has already submitted.
 */
export function canonicalPostId(platform: Platform, videoId: string): string {
  const value = videoId.trim();
  if (platform !== 'TIKTOK') return value;
  /* `(?=\d)` so an id of all zeros does not become the empty string. */
  return value.replace(/^0+(?=\d)/, '');
}

/* ------------------------------------------------------------------ */
/* Caption rules                                                       */
/* ------------------------------------------------------------------ */

/**
 * What a campaign requires the caption to carry.
 *
 * Stored as JSON in `Campaign.captionRulesJson` and copied into every frozen
 * `CampaignTermsVersion`, because "you must credit the channel" is a rule a
 * creator accepted on a date and a rule money later depends on — not prose the
 * next edit can quietly drop.
 */
export interface CaptionRules {
  /** Hashtags that must appear, stored with the `#`, lower-cased. */
  requiredHashtags: string[];
  /** An account that must be credited, stored with the `@`, lower-cased. */
  requiredMention: string | null;
  /** Whether the post must be disclosed as paid (#ad and friends). */
  requiresDisclosure: boolean;
}

export const EMPTY_CAPTION_RULES: CaptionRules = Object.freeze({
  requiredHashtags: [],
  requiredMention: null,
  requiresDisclosure: false,
});

/** Are any caption rules actually stated? Nothing runs when nothing is asked for. */
export function captionRulesStated(rules: CaptionRules): boolean {
  return rules.requiredHashtags.length > 0 || rules.requiredMention !== null;
}

/**
 * The tags that count as a paid disclosure, as the platforms' own rules and the
 * FTC's guidance name them.
 */
export const DISCLOSURE_HASHTAGS: readonly string[] = Object.freeze([
  '#ad',
  '#ads',
  '#advert',
  '#sponsored',
  '#paid',
  '#paidpartnership',
  '#paidad',
]);

/** Phrases that disclose a paid post without a hashtag. */
export const DISCLOSURE_PHRASES: readonly string[] = Object.freeze([
  'paid partnership',
  'paid promotion',
]);

/**
 * Flatten a caption the way a comparison has to see it.
 *
 * NFKC because platforms substitute full-width and styled characters into what
 * the creator typed, and the zero-width family because an emoji keyboard and a
 * paste between apps both leave them behind. A creator who did exactly what
 * they were asked must not fail a check over an invisible character.
 */
function flatten(value: string): string {
  return value
    .normalize('NFKC')
    /* The INVISIBLES only -- NBSP, the zero-width family, the word joiner, the
       BOM -- because an emoji keyboard and a paste between apps both leave them
       behind. Ordinary whitespace is kept and collapsed instead: "paid
       partnership" is two words, and stripping the space between them would
       make the phrase unfindable. */
    .replace(/[\u00a0\u180e\u200b-\u200f\u2060\ufeff]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/**
 * The hashtags in a caption, as whole tokens.
 *
 * TOKENS AND NOT SUBSTRINGS, because `#ad` is a substring of `#adventure` and a
 * substring test would pass a post that disclosed nothing. The same reasoning
 * applies to a required tag: `#jag` must not be satisfied by `#jaguar`.
 */
export function captionHashtags(caption: string): string[] {
  return [...flatten(caption).matchAll(/#[\p{L}\p{N}_]+/gu)].map((m) => m[0]);
}

/** The @mentions in a caption, as whole tokens. */
export function captionMentions(caption: string): string[] {
  return [...flatten(caption).matchAll(/@[\p{L}\p{N}_.]+/gu)].map((m) => m[0].replace(/\.+$/, ''));
}

/** Normalise what an operator typed into the form the comparison uses. */
export function normalizeRequiredHashtag(raw: string): string | null {
  const flat = flatten(raw).replace(/^#+/, '');
  const match = /^[\p{L}\p{N}_]+$/u.exec(flat);
  return match ? `#${flat}` : null;
}

export function normalizeRequiredMention(raw: string): string | null {
  const flat = flatten(raw).replace(/^@+/, '');
  const match = /^[\p{L}\p{N}_.]+$/u.exec(flat);
  return match ? `@${flat}` : null;
}

export interface CaptionVerdict {
  /** Required hashtags the caption does not carry. */
  missingHashtags: string[];
  /** The required credit, when it is missing. */
  missingMention: string | null;
  ok: boolean;
}

export function evaluateCaptionRules(caption: string, rules: CaptionRules): CaptionVerdict {
  const present = new Set(captionHashtags(caption));
  const mentions = new Set(captionMentions(caption));
  const missingHashtags = rules.requiredHashtags.filter((tag) => !present.has(tag));
  const missingMention =
    rules.requiredMention && !mentions.has(rules.requiredMention) ? rules.requiredMention : null;
  return {
    missingHashtags,
    missingMention,
    ok: missingHashtags.length === 0 && missingMention === null,
  };
}

export function evaluateDisclosure(caption: string): { disclosed: boolean } {
  const tags = new Set(captionHashtags(caption));
  if (DISCLOSURE_HASHTAGS.some((tag) => tags.has(tag))) return { disclosed: true };
  const flat = flatten(caption);
  return { disclosed: DISCLOSURE_PHRASES.some((phrase) => flat.includes(phrase)) };
}

/** The campaign's caption rules on one line, for Telegram and the review queue. */
export function captionRulesSentence(rules: CaptionRules): string {
  const parts: string[] = [];
  if (rules.requiredHashtags.length > 0) parts.push(rules.requiredHashtags.join(' '));
  if (rules.requiredMention) parts.push(`credit ${rules.requiredMention}`);
  if (rules.requiresDisclosure) parts.push('disclosed as paid');
  return parts.length > 0 ? parts.join(', ') : 'no caption rules';
}

/* ------------------------------------------------------------------ */
/* The five words a creator reads on their own history                 */
/* ------------------------------------------------------------------ */

/**
 * What a creator calls the state of a submission.
 *
 * `SubmissionStatus` is the DATABASE's vocabulary: five values pinned by a
 * CHECK constraint, two of which (`PENDING_CHECKS`, `IN_REVIEW`) are the same
 * fact to the person waiting — nobody has ever asked "is my clip running the
 * automatic checks or sitting in content review", they ask "has anyone looked
 * at it yet". And one fact a creator cares about a great deal is not in that
 * column at all: whether the money has actually been paid, which lives on the
 * `Earning`.
 *
 * So this is the creator's own five-word vocabulary, derived from the two rows
 * together, and it is what the filter on "My submissions" offers and what the
 * pill on each card says. The mapping is HERE, in one place shared by the API's
 * filter and the web's labels, so a filter that says "Approved" and a pill that
 * says "Approved" can never come to mean different sets of rows.
 */
export const CREATOR_SUBMISSION_STATES = ['PENDING', 'APPROVED', 'PAID', 'REJECTED', 'HELD'] as const;
export type CreatorSubmissionState = (typeof CREATOR_SUBMISSION_STATES)[number];

export const CREATOR_SUBMISSION_STATE_LABELS: Readonly<
  Record<CreatorSubmissionState, string>
> = Object.freeze({
  PENDING: 'Pending review',
  APPROVED: 'Approved',
  PAID: 'Paid',
  REJECTED: 'Rejected',
  HELD: 'Held',
});

/** One sentence per state, for the filter and for a creator who hovers it. */
export const CREATOR_SUBMISSION_STATE_NOTES: Readonly<
  Record<CreatorSubmissionState, string>
> = Object.freeze({
  PENDING: 'Submitted. The checks have run or are running, and an operator still has to look at it.',
  APPROVED: 'Accepted. It earns on its verified views once the measurement window closes.',
  PAID: 'Approved, and the money for it has left in a payout.',
  REJECTED: 'Not accepted. The reason is on the card, and it earns nothing.',
  HELD: 'Waiting on a person. You do not need to repost — the reason is on the card.',
});

/**
 * The creator-facing state of one submission.
 *
 * `earningStatus` is the `Earning` row's status when there is one. It is the
 * only thing that separates APPROVED from PAID, and it is deliberately the
 * LAST thing consulted: a submission that was rejected or held is that,
 * whatever any earning row says.
 */
export function creatorSubmissionState(input: {
  status: SubmissionStatus;
  earningStatus?: 'APPROVED' | 'REVERSED' | 'PAID' | null;
}): CreatorSubmissionState {
  switch (input.status) {
    case 'REJECTED':
      return 'REJECTED';
    case 'HELD':
      return 'HELD';
    case 'PENDING_CHECKS':
    case 'IN_REVIEW':
      return 'PENDING';
    case 'VERIFIED':
      return input.earningStatus === 'PAID' ? 'PAID' : 'APPROVED';
  }
}

/**
 * The database statuses one creator-facing state covers.
 *
 * PAID is a subset of VERIFIED that the submission row alone cannot express, so
 * it maps to the same statuses and the API narrows it with the earning. Listed
 * here anyway so the filter has one table and not two.
 */
export const CREATOR_STATE_STATUSES: Readonly<
  Record<CreatorSubmissionState, readonly SubmissionStatus[]>
> = Object.freeze({
  PENDING: ['PENDING_CHECKS', 'IN_REVIEW'],
  APPROVED: ['VERIFIED'],
  PAID: ['VERIFIED'],
  REJECTED: ['REJECTED'],
  HELD: ['HELD'],
});

/* ------------------------------------------------------------------ */
/* How a creator orders their own history                              */
/* ------------------------------------------------------------------ */

/**
 * The four orders "My submissions" offers, and the only four the API accepts.
 *
 * ORDER IS PART OF THE QUERY, never of the page: the sort decides which rows
 * the LIMIT keeps, so a page that fetched fifty rows newest-first and then
 * re-sorted them by views in JavaScript would show "most views" over a sample
 * that had already thrown the best clip away.
 */
export const SUBMISSION_SORTS = ['newest', 'oldest', 'views', 'payout'] as const;
export type SubmissionSort = (typeof SUBMISSION_SORTS)[number];

export const SUBMISSION_SORT_LABELS: Readonly<Record<SubmissionSort, string>> = Object.freeze({
  newest: 'Newest first',
  oldest: 'Oldest first',
  views: 'Most views',
  payout: 'Highest payout',
});

/** The page size "My submissions" asks for, and the most the API will serve. */
export const SUBMISSIONS_PAGE_SIZE = 50;
export const SUBMISSIONS_MAX_PAGE_SIZE = 100;
